// Events.jsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Added
import { motion } from 'framer-motion';
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

// === HERO IMAGE ===
import eventsHeroImage from '../assets/Sonic/vesak-festival-in-sri-lanka.jpg';

// === EVENT IMAGES ===
import event1 from '../assets/pick/9.jpeg';
import event4 from '../assets/pick/10.jpeg';
import event2 from '../assets/pick/11.jpeg';
import event5 from '../assets/pick/12.jpg';
import event6 from '../assets/pick/15.jpg';
import event8 from '../assets/pick/16.jpeg';
import event3 from '../assets/pick/17.jpg';
import event7 from '../assets/pick/18.webp';

// === FRAMER MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } },
};

const Events = () => {
    const { t } = useTranslation('events'); // Use events namespace
    const [index, setIndex] = useState(-1);
    const location = useLocation();

    // === EVENTS DATA (labels from i18n) ===
    const events = [
        { image: event1, label: t('kandyPerahera'), span: "col-span-1 row-span-1" },
        { image: event4, label: t('vesak'), span: "col-span-1 row-span-2" },
        { image: event2, label: t('sinhalaNewYear'), span: "col-span-1 row-span-1" },
        { image: event5, label: t('christmas'), span: "col-span-1 row-span-2" },
        { image: event6, label: t('thaiPongal'), span: "col-span-1 row-span-2" },
        { image: event8, label: t('deepavali'), span: "col-span-1 row-span-2" },
        { image: event3, label: t('holi'), span: "col-span-1 row-span-1" },
        { image: event7, label: t('poson'), span: "col-span-1 row-span-1" },
    ];

    // Prepare slides for Lightbox
    const slides = events.map((item) => ({
        src: item.image,
        width: 1600,
        height: 1067,
        alt: item.label,
        title: item.label,
    }));

    return (
        <div className="bg-[#001F3F] min-h-screen">
            {/* HERO SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative h-[700px] w-full bg-cover bg-left md:h-screen md:max-h-[740px] md:bg-center lg:max-h-[800px] xl:max-h-[960px] overflow-hidden"
                style={{ backgroundImage: `url(${eventsHeroImage})` }}
            >
                <div className="absolute inset-0 bg-gradient-to-b from-black/85 via-black/60 to-black/85" />

                <div className="container relative mx-auto px-4 h-full flex items-center">
                    <div className="flex flex-col items-start max-w-4xl">
                        <h1
                            className="font-konkhmer text-[3.125rem] uppercase md:text-5xl lg:text-[5.5rem] xl:text-[6.5rem] font-bold leading-tight text-[#7FDBFF]"

                        >
                            {t('heroTitle')}
                        </h1>

                        <div className="mt-6 lg:mt-8">
                            <p className="max-w-[700px] text-left font-inknut text-sm leading-7 text-white/90 md:text-base lg:text-lg lg:leading-8">
                                {t('heroSubtitle')}
                            </p>


                        </div>
                    </div>
                </div>


            </motion.section>

            {/* EVENTS GRID SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="py-16 lg:py-24"
            >
                <div className="container mx-auto px-4">
                    {/* Tabs */}
                    <div className="flex items-center justify-center gap-6 font-redHatDisplay text-2xl font-bold uppercase md:text-3xl lg:text-4xl">
                        <Link
                            to="/gallery"
                            className={`pb-3 border-b-4 transition-all duration-300 ${location.pathname === "/gallery"
                                ? "border-[#7FDBFF] text-[#7FDBFF]"
                                : "border-transparent text-white/70 hover:text-[#0074D9]"
                                }`}
                        >
                            {t('tabPlaces')}
                        </Link>
                        <span className="text-white/40 text-3xl">/</span>
                        <Link
                            to="/events"
                            className={`pb-3 border-b-4 transition-all duration-300 ${location.pathname === "/events"
                                ? "border-[#7FDBFF] text-[#7FDBFF]"
                                : "border-transparent text-white/70 hover:text-[#0074D9]"
                                }`}
                        >
                            {t('tabEvents')}
                        </Link>
                    </div>

                    {/* Grid */}
                    <div className="mt-16 grid auto-rows-[200px] grid-cols-2 gap-4 md:auto-rows-[260px] md:grid-cols-3 md:gap-5 lg:auto-rows-[480px] lg:gap-7 xl:gap-8">
                        {events.map((item, i) => (
                            <motion.button
                                key={i}
                                whileHover={{ scale: 1.05, y: -6 }}
                                whileTap={{ scale: 0.96 }}
                                type="button"
                                onClick={() => setIndex(i)}
                                className={`group relative overflow-hidden rounded-2xl shadow-2xl transition-all duration-500 ${item.span || ''}`}
                                aria-label={`Open ${i + 1}`}
                            >
                                <img
                                    src={item.image}
                                    alt={item.label}
                                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-115"
                                    loading="lazy"
                                />

                                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                    <div className="absolute bottom-0 left-0 right-0 p-6 text-left">
                                        <h3 className="font-konkhmer text-lg font-bold uppercase tracking-wider text-white drop-shadow-xl md:text-xl lg:text-2xl">
                                            {item.label}
                                        </h3>
                                    </div>
                                </div>
                            </motion.button>
                        ))}
                    </div>
                </div>

                {/* LIGHTBOX */}
                <Lightbox
                    open={index >= 0}
                    close={() => setIndex(-1)}
                    index={index}
                    slides={slides}
                    plugins={[Thumbnails, Zoom]}
                    thumbnails={{
                        position: "bottom",
                        width: 110,
                        height: 80,
                        border: 3,
                        borderColor: "transparent",
                        borderRadius: 12,
                        padding: 6,
                        gap: 16,
                        imageFit: "cover",
                    }}
                    zoom={{ maxZoomPixelRatio: 4 }}
                    styles={{
                        container: { backgroundColor: "rgba(0, 31, 63, 0.98)" },
                        thumbnail: {
                            cursor: "pointer",
                            background: "linear-gradient(45deg, #0074D9, #7FDBFF)",
                            borderRadius: "12px",
                            overflow: "hidden",
                            boxShadow: "0 4px 15px rgba(0,0,0,0.4)",
                        },
                        thumbnailsContainer: {
                            backgroundColor: "rgba(0, 31, 63, 0.9)",
                            padding: "12px 0",
                            borderTop: "1px solid rgba(255,255,255,0.1)",
                        },
                    }}
                    labels={{
                        close: t('close') || "Close",
                        next: t('next') || "Next",
                        previous: t('previous') || "Previous",
                    }}
                    controller={{ closeOnBackdropClick: true }}
                />
            </motion.section>
        </div>
    );
};

export default Events;